import { jwtDecode } from 'jwt-decode';
import {
  DEFAULT_TOKEN_CACHE_KEY,
  ID_TOKEN_STORAGE_KEY,
  LOGGED_USER_STORAGE_KEY,
} from './constants';
import type { LoggedUser, StoredLoggedUser } from '../types';

/**
 * localStorage-backed auth bookkeeping, ported from the old app's utils/utils.js.
 *
 * Deliberately kept as localStorage rather than sessionStorage or an httpOnly cookie —
 * an explicit parity decision. The Okta tokenManager is configured for localStorage in
 * services/okta.ts for the same reason (AuthService.js:15). Do not "improve" this
 * without a separate decision: it changes the session lifetime users experience, and the
 * `?jwt=` deep link from Empower depends on writing straight into this store.
 */

let tokenCacheKey = DEFAULT_TOKEN_CACHE_KEY;

/** Called once /api/auth-config resolves, so later reads/writes use the configured key. */
export function setTokenCacheKey(key: string): void {
  tokenCacheKey = key || DEFAULT_TOKEN_CACHE_KEY;
}

export function getTokenCacheKey(): string {
  return tokenCacheKey;
}

/** utils.js:setAccessToken. */
export function setAccessToken(accessToken: string): void {
  localStorage.setItem(tokenCacheKey, accessToken);
}

export function getAccessToken(): string | null {
  return localStorage.getItem(tokenCacheKey);
}

interface DecodedToken {
  name?: string;
  preferred_username?: string;
  email?: string;
  exp?: number;
  [key: string]: unknown;
}

/**
 * utils.js:setloggedUserDetails — including its storage shape: `{name, userName}` under
 * `logged-user`, plus the raw id token under `idToken`.
 *
 * Throws if `idToken` is not a decodable JWT, matching the old behavior where
 * `jwt(null)` threw into App.js's catch and produced the generic error modal.
 */
export function setLoggedUserDetails(idToken: string): void {
  const decoded = jwtDecode<DecodedToken>(idToken);
  const stored: StoredLoggedUser = {
    name: decoded.name ?? '',
    userName: decoded.preferred_username ?? decoded.email ?? '',
  };
  localStorage.setItem(LOGGED_USER_STORAGE_KEY, JSON.stringify(stored));
  localStorage.setItem(ID_TOKEN_STORAGE_KEY, idToken);
}

/**
 * utils.js:getloggedUserDetails — the raw `{name, userName}` shape.
 *
 * The original was `JSON.parse(localStorage.getItem("logged-user"))`, which returns null
 * for a missing key and throws on malformed JSON. Call sites then did
 * `getloggedUserDetails().name` unguarded, so a missing key threw a TypeError into the
 * surrounding catch. Returning null here reaches the same call sites, which are ported
 * with the same optional-chaining the original had at each one.
 */
export function getStoredLoggedUser(): StoredLoggedUser | null {
  const raw = localStorage.getItem(LOGGED_USER_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredLoggedUser;
  } catch {
    return null;
  }
}

/** The `{displayName, email}` prop shape components expect (App.js:55-58). */
export function getLoggedUserDetails(): LoggedUser | null {
  const stored = getStoredLoggedUser();
  if (!stored) return null;
  return { displayName: stored.name, email: stored.userName };
}

export function getIdToken(): string | null {
  return localStorage.getItem(ID_TOKEN_STORAGE_KEY);
}

/** utils.js:33 — `Date.now() >= jwt(token).exp * 1000`, with decode failure as expired. */
export function isTokenExpired(token: string): boolean {
  try {
    const decoded = jwtDecode<DecodedToken>(token);
    if (!decoded.exp) return true;
    return Date.now() >= decoded.exp * 1000;
  } catch {
    return true;
  }
}

/** AuthService.logout()'s localStorage.clear(). */
export function clearAuthStorage(): void {
  localStorage.clear();
}
